import { Observable, Subject } from 'rxjs';

import { Injectable } from '@angular/core';

import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';
import {
  FsMapPopup,
  IMapMarkerStyleOptions,
  IMarker as _IMarker,
  MapMarkerIcon,
} from '@lib/geo/map';
import { IMarkerFacade } from '@lib/geo/map/interfaces/markers/map-marker-facade.interface';
import {
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from '@lib/geo/map/interfaces/markers/marker-events.interface';
import { IMarkerOptions } from '@lib/geo/map/interfaces/markers/marker-options.interface';
import { DistanceUnit, MapGeometryFacadeService } from '@lib/map-geometry';
import { SnBadgeConfig } from '@lib/ui/badge';

import { GoogleMap } from '../../map';
import { FsAdvancedFeatureMarker, IAdvancedFeatureOptions } from './advanced-feature-marker';
import { IGoogleMarker } from './interfaces/google-marker.interface';
import { IGoogleMapMarkerLabel } from './marker-options';
import { GoogleMapMarkerType } from './marker-types';
import {
  GoogleMarkerBadgeService,
  GoogleMarkerEventService,
  GoogleMarkerGeometryService,
  GoogleMarkerQueryService,
  GoogleMarkerStyleService,
} from './services';
import { GoogleMarkerFactoryService } from './services/google-marker-factory.service';

type IMarker = _IMarker<GoogleMap>;
/**
 * Adapter class that implements IMarkerFacade for Google Maps markers.
 *
 * Bridges between the unified IMarkerFacade interface and Google Maps implementation.
 * Uses FsAdvancedMarker instances with embedded IMarker data for state management.
 */
@Injectable()
export class GoogleMarkerAdapter implements IMarkerFacade<GoogleMap> {
  private readonly _googleMarkers = new Map<string, IGoogleMarker>();
  private readonly _destroy$ = new Subject<void>();
  private _map?: GoogleMap;

  constructor(
    private readonly _badgeService: GoogleMarkerBadgeService,
    private readonly _eventService: GoogleMarkerEventService,
    private readonly _geometryService: GoogleMarkerGeometryService,
    private readonly _queryService: GoogleMarkerQueryService,
    private readonly _styleService: GoogleMarkerStyleService,
    private readonly _mapGeometryService: MapGeometryFacadeService<GoogleMap>,
    private readonly _markerFactory: GoogleMarkerFactoryService,
  ) {
    this._queryService.setMarkersCollection(this._googleMarkers);
    this._eventService.setMarkersCollection(this._googleMarkers);
    this._geometryService.setMarkersCollection(this._googleMarkers);
  }

  setMap(map: GoogleMap): void {
    this._map = map;
    this._mapGeometryService.setMap(map);
  }

  addMarker(markerOptions: IMarkerOptions): IMarker {
    const markerId = this._generateMarkerId();
    const advancedFeatureOptions = this._convertToAdvancedFeatureMarkerOptions(markerOptions);
    const googleMarker = this._markerFactory.createFeatureMarker(advancedFeatureOptions);
    const markerData = this._createMarkerData(
      markerId,
      markerOptions,
      googleMarker as unknown as IGoogleMarker,
    );

    (googleMarker as unknown as FsAdvancedFeatureMarker & { markerData: IMarker }).markerData =
      markerData;
    this._googleMarkers.set(markerId, googleMarker as unknown as IGoogleMarker);
    if (this._map) {
      googleMarker.addTo(this._map);
    }
    this._eventService.fireMarkerEvent('add', markerData, { reason: 'programmatic' });

    return markerData;
  }

  removeMarker(marker: IMarker): void {
    const markerId = marker.id;
    const googleMarker = this._googleMarkers.get(markerId);

    if (googleMarker) {
      this._eventService.fireMarkerEvent('remove', marker, { reason: 'programmatic' });
      googleMarker.remove();
      this._googleMarkers.delete(markerId);
      this._eventService.fireMarkerEvent('destroy', marker, { reason: 'programmatic' });
    }
  }

  updateMarker(marker: IMarker, options: Partial<IMarkerOptions>): void {
    const markerId = marker.id;
    const googleMarker = this._googleMarkers.get(markerId);

    if (googleMarker) {
      if (options.lonLat) {
        this._geometryService.setPosition(googleMarker, marker, SnLonLat.convert(options.lonLat));
      }
      if (options.icon) {
        this._styleService.setIcon(googleMarker, marker, options.icon);
      }
      if (options.style) {
        this._styleService.setStyle(googleMarker, marker, options.style);
      }
      if (options.draggable !== undefined) {
        this._styleService.setDraggable(googleMarker, marker, options.draggable);
      }
      if (options.rotation !== undefined) {
        this._styleService.setRotation(googleMarker, marker, options.rotation);
      }
      if (options.title) {
        this._styleService.setTitle(googleMarker, marker, options.title);
      }

      Object.assign(marker, options);
      marker.lastModified = new Date();
      marker.version++;
    }
  }

  setVisible(marker: IMarker, visible: boolean): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      const previousVisible = marker.visible;
      this._geometryService.setVisible(googleMarker, marker, visible);

      if (previousVisible !== visible) {
        this._eventService.fireMarkerEvent('visibilitychange', marker, {
          current: visible,
          previous: previousVisible,
          changed: true,
        });
      }
    }
  }

  setPosition(marker: IMarker, position: SnLonLat): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      const previousPosition = marker.position;
      this._geometryService.setPosition(googleMarker, marker, position);

      this._eventService.fireMarkerEvent('positionchange', marker, {
        current: position,
        previous: previousPosition,
        changed: true,
      });
    }
  }

  setIcon(marker: IMarker, icon: MapMarkerIcon): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      const previousIcon = marker.icon;
      this._styleService.setIcon(googleMarker, marker, icon);

      this._eventService.fireMarkerEvent('iconchange', marker, {
        current: icon,
        previous: previousIcon,
        changed: true,
      });
    }
  }

  setStyle(marker: IMarker, style: IMapMarkerStyleOptions): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      const previousStyle = marker.styles;
      this._styleService.setStyle(googleMarker, marker, style);

      this._eventService.fireMarkerEvent('stylechange', marker, {
        current: style,
        previous: previousStyle,
        changed: true,
      });
    }
  }

  setDraggable(marker: IMarker, draggable: boolean): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setDraggable(googleMarker, marker, draggable);
    }
  }

  setRotation(marker: IMarker, rotation: number): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setRotation(googleMarker, marker, rotation);
    }
  }

  setTitle(marker: IMarker, title: string): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      const previousTitle = marker.title;
      this._styleService.setTitle(googleMarker, marker, title);

      if (previousTitle !== title) {
        this._eventService.fireMarkerEvent('titlechange', marker, {
          current: title,
          previous: previousTitle,
          changed: true,
        });
      }
    }
  }

  getMarkerId(marker: IMarker): string {
    return marker.id;
  }

  getMarkerPosition(marker: IMarker): SnLonLat {
    return marker.position;
  }

  getMarkerTitle(marker: IMarker): string {
    return marker.title;
  }

  getMarkerAttributes(marker: IMarker): Record<string, unknown> {
    return marker.attributes || {};
  }

  getMarkerCenter(marker: IMarker): { center: number[] } {
    return this._geometryService.getMarkerCenter(marker);
  }

  getMarkerGeometry(marker: IMarker): unknown {
    return this._geometryService.getMarkerGeometry(marker);
  }

  setBadge(marker: IMarker, badge: SnBadgeConfig): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._badgeService.setBadge(googleMarker, marker, badge);
    }
  }

  getBadge(marker: IMarker): SnBadgeConfig {
    return this._badgeService.getBadge(marker);
  }

  createBadge(marker: IMarker, badgeClass?: string): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._badgeService.createBadge(googleMarker, marker, badgeClass);
    }
  }

  createSelectedBadge(marker: IMarker): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._badgeService.createSelectedBadge(googleMarker, marker);
    }
  }

  removeSelectedBadge(marker: IMarker): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._badgeService.removeSelectedBadge(googleMarker, marker);
    }
  }

  setBadgeStatus(marker: IMarker, status: string, badgePosition: Record<string, unknown>): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._badgeService.setBadgeStatus(googleMarker, marker, status, badgePosition);
    }
  }

  setPopup(marker: IMarker, popup: FsMapPopup<GoogleMap>): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      marker.popup = popup;
      this._setupMarkerPopupClickHandler(googleMarker, marker, popup);
    }
  }

  private _setupMarkerPopupClickHandler(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    popup: FsMapPopup<GoogleMap>,
  ): void {
    const googleMapsMarker = googleMarker.getMarker();
    googleMapsMarker.addListener('click', () => {
      popup.open();
    });
  }

  onMarkerClick(marker: IMarker): Observable<IMarkerClickEvent<GoogleMap>> {
    return this._eventService.onMarkerClick(marker);
  }

  onMarkerDoubleClick(marker: IMarker): Observable<IMarkerDoubleClickEvent<GoogleMap>> {
    return this._eventService.onMarkerDoubleClick(marker);
  }

  onMarkerRightClick(marker: IMarker): Observable<IMarkerRightClickEvent<GoogleMap>> {
    return this._eventService.onMarkerRightClick(marker);
  }

  onMarkerMouseOver(marker: IMarker): Observable<IMarkerMouseOverEvent<GoogleMap>> {
    return this._eventService.onMarkerMouseOver(marker);
  }

  onMarkerMouseOut(marker: IMarker): Observable<IMarkerMouseOutEvent<GoogleMap>> {
    return this._eventService.onMarkerMouseOut(marker);
  }

  onMarkerMouseEnter(marker: IMarker): Observable<IMarkerMouseEnterEvent<GoogleMap>> {
    return this._eventService.onMarkerMouseEnter(marker);
  }

  onMarkerMouseLeave(marker: IMarker): Observable<IMarkerMouseLeaveEvent<GoogleMap>> {
    return this._eventService.onMarkerMouseLeave(marker);
  }

  onMarkerDragStart(marker: IMarker): Observable<IMarkerDragStartEvent<GoogleMap>> {
    return this._eventService.onMarkerDragStart(marker);
  }

  onMarkerDrag(marker: IMarker): Observable<IMarkerDragMoveEvent<GoogleMap>> {
    return this._eventService.onMarkerDrag(marker);
  }

  onMarkerDragEnd(marker: IMarker): Observable<IMarkerDragEndEvent<GoogleMap>> {
    return this._eventService.onMarkerDragEnd(marker);
  }

  onMarkerPositionChange(marker: IMarker): Observable<IMarkerPositionChangeEvent<GoogleMap>> {
    return this._eventService.onMarkerPositionChange(marker);
  }

  onMarkerTitleChange(marker: IMarker): Observable<IMarkerTitleChangeEvent<GoogleMap>> {
    return this._eventService.onMarkerTitleChange(marker);
  }

  onMarkerVisibilityChange(marker: IMarker): Observable<IMarkerVisibilityChangeEvent<GoogleMap>> {
    return this._eventService.onMarkerVisibilityChange(marker);
  }

  onMarkerSelectionChange(marker: IMarker): Observable<IMarkerSelectionChangeEvent<GoogleMap>> {
    return this._eventService.onMarkerSelectionChange(marker);
  }

  onMarkerIconChange(marker: IMarker): Observable<IMarkerIconChangeEvent<GoogleMap>> {
    return this._eventService.onMarkerIconChange(marker);
  }

  onMarkerStyleChange(marker: IMarker): Observable<IMarkerStyleChangeEvent<GoogleMap>> {
    return this._eventService.onMarkerStyleChange(marker);
  }

  onMarkerAdd(marker: IMarker): Observable<IMarkerAddEvent<GoogleMap>> {
    return this._eventService.onMarkerAdd(marker);
  }

  onMarkerRemove(marker: IMarker): Observable<IMarkerRemoveEvent<GoogleMap>> {
    return this._eventService.onMarkerRemove(marker);
  }

  onMarkerDestroy(marker: IMarker): Observable<IMarkerDestroyEvent<GoogleMap>> {
    return this._eventService.onMarkerDestroy(marker);
  }

  onMarkerEvents(marker: IMarker): Observable<IMarkerEventUnion<GoogleMap>> {
    return this._eventService.onMarkerEvents(marker);
  }

  findMarkerById(id: string): IMarker | null {
    return this._queryService.findMarkerById(id);
  }

  getAllMarkers(): IMarker[] {
    return this._queryService.getAllMarkers();
  }

  getMarkersByAttribute(attribute: string, value: unknown): IMarker[] {
    return this._queryService.getMarkersByAttribute(attribute, value);
  }

  private _generateMarkerId(): string {
    return `marker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private _createMarkerData(
    id: string,
    options: IMarkerOptions,
    googleMarker: IGoogleMarker,
  ): IMarker {
    return {
      id,
      position: options.lonLat ? SnLonLat.convert(options.lonLat) : new SnLonLat(0, 0),
      visible: true,
      icon: options.icon,
      color: options.color,
      size: options.size,
      rotation: options.rotation,
      draggable: options.draggable ?? false,
      clickable: options.clickable ?? true,
      title: options.title,
      content: options.content,
      zIndex: options.zIndex,
      styles: options.style,
      customClass: options.animationClass,
      customStyle: {},
      iconLabel: options.iconLabel,
      tooltip: options.showTooltipOnHover ? 'hover' : undefined,
      attributes: options.attributes,
      metadata: {
        entityType: options.cluster?.properties?.objects ? 'cluster' : 'marker',
        entityId: options.cluster?.id?.toString() || id,
        source: 'google-marker-adapter',
        tags: [],
        category: 'marker',
        priority: options.priority === 'high' ? 1 : options.priority === 'low' ? 3 : 2,
        customData: {
          clusterObjects: options.cluster?.properties?.objects || 0,
        },
      },
      options,
      badge: undefined,
      popup: undefined,
      selected: options.initialSelected ?? false,
      highlighted: options.initialHighlighted ?? false,
      focused: options.initialFocused ?? false,
      priority: options.priority ?? 'normal',
      lazyLoaded: options.lazyLoad ?? false,
      eventHandlers: {
        click: false,
        doubleClick: false,
        rightClick: false,
        mouseOver: false,
        mouseOut: false,
        mouseEnter: false,
        mouseLeave: false,
        dragStart: false,
        drag: false,
        dragEnd: false,
        focus: false,
        blur: false,
        positionChange: false,
        titleChange: false,
        visibilityChange: false,
        selectionChange: false,
        iconChange: false,
        styleChange: false,
        ...options.eventHandlers,
      },
      ariaLabel: options.title,
      role: options.clickable ? 'button' : 'img',
      tabIndex: options.clickable ? 0 : -1,
      created: new Date(),
      lastModified: new Date(),
      version: 1,
    };
  }

  private _convertToAdvancedFeatureMarkerOptions(options: IMarkerOptions): IAdvancedFeatureOptions {
    const position = options.lonLat ? SnLonLat.convert(options.lonLat) : undefined;
    const latLng = position ? new google.maps.LatLng(position.lat, position.lon) : undefined;

    // Convert icon to IAdvancedIconOptions format
    let icon: { fontSet?: string; fontIcon?: string; color?: string; url?: string } | undefined;
    if (options.icon && typeof options.icon === 'object') {
      if ('fontIcon' in options.icon && 'fontSet' in options.icon) {
        icon = {
          fontSet: options.icon.fontSet === 'fusus' ? 'fs' : options.icon.fontSet,
          fontIcon: options.icon.fontIcon,
          color: options.color,
        };
      } else if ('url' in options.icon) {
        icon = {
          url: options.icon.url,
        };
      }
    }

    // Convert styles to IAdvancedStyleOptions format
    const styles = options.style
      ? {
          fillOpacity: options.style.fillOpacity ?? 0,
          strokeOpacity: options.style.strokeOpacity ?? 0,
          strokeWeight: options.style.strokeWeight,
          strokeColor: options.style.strokeColor,
          fillColor: options.style.fillColor,
          borderRadius: options.style.borderRadius,
          scale: options.style.scale,
        }
      : undefined;

    return {
      id: options.id,
      icon,
      title: options.title || '',
      position: latLng,
      draggable: options.draggable ?? false,
      zIndex: options.zIndex ?? 0,
      size: options.size || '24px',
      color: options.color || '#fff',
      rotation: options.style?.rotation ?? 0,
      outline: false,
      styles,
    };
  }

  calculateDistance(
    marker1: IMarker,
    marker2: IMarker,
    unit: DistanceUnit = DistanceUnit.MILES,
  ): number {
    const googleMarker1 = this._googleMarkers.get(marker1.id);
    const googleMarker2 = this._googleMarkers.get(marker2.id);
    if (googleMarker1 && googleMarker2) {
      return this._geometryService.calculateDistance(marker1, marker2, unit);
    }
    return 0;
  }

  setFollowMode(marker: IMarker, followMode: boolean): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._geometryService.setFollowMode(googleMarker, marker, followMode);
    }
  }

  getFollowMode(marker: IMarker): boolean {
    return this._geometryService.getFollowMode(marker);
  }

  setDestination(marker: IMarker, dest: SnLonLatLike): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._geometryService.setDestination(googleMarker, marker, dest);
    }
  }

  setDeltas(marker: IMarker, seconds: number): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._geometryService.setDeltas(googleMarker, marker, seconds);
    }
  }

  move(marker: IMarker): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._geometryService.move(googleMarker, marker);
    }
  }

  animateSetLonLat(marker: IMarker, seconds: number, moveto: SnLonLatLike): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._geometryService.animateSetLonLat(googleMarker, marker, seconds, moveto);
    }
  }

  isWithinMapBounds(marker: IMarker): boolean {
    return this._geometryService.isWithinMapBounds(marker);
  }

  getAnimationState(marker: IMarker): {
    isAnimating: boolean;
    destination?: SnLonLat;
    seconds?: number;
  } {
    return this._geometryService.getAnimationState(marker);
  }

  setType(marker: IMarker, type: GoogleMapMarkerType): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setType(googleMarker, marker, type);
    }
  }

  getType(marker: IMarker): GoogleMapMarkerType | undefined {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      return this._styleService.getType(googleMarker);
    }
    return undefined;
  }

  getFeatures(marker: IMarker): unknown {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      return this._styleService.getFeatures(googleMarker);
    }
    return undefined;
  }

  getIconLabel(marker: IMarker): unknown {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      return this._styleService.getIconLabel(googleMarker);
    }
    return undefined;
  }

  setLabel(marker: IMarker, label: string | IGoogleMapMarkerLabel): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setLabel(googleMarker, marker, label);
    }
  }

  getLabel(marker: IMarker): IGoogleMapMarkerLabel | undefined {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      return this._styleService.getLabel(googleMarker);
    }
    return undefined;
  }

  setPulse(
    marker: IMarker,
    colors: string[],
    delay: number,
    fontSet?: string,
    fontIcon?: string,
  ): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setPulse(googleMarker, marker, colors, delay, fontSet, fontIcon);
    }
  }

  stopPulse(marker: IMarker): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.stopPulse(googleMarker, marker);
    }
  }

  getPulseState(marker: IMarker): { active: boolean; colors?: string[]; delay?: number } {
    return this._styleService.getPulseState(marker);
  }

  setAddress(marker: IMarker, address: string): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setAddress(googleMarker, marker, address);
    }
  }

  getAddress(marker: IMarker): string | undefined {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      return this._styleService.getAddress(googleMarker);
    }
    return undefined;
  }

  setFeature(marker: IMarker, feature: unknown): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setFeature(googleMarker, marker, feature);
    }
  }

  getFeature(marker: IMarker): unknown {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      return this._styleService.getFeature(googleMarker);
    }
    return undefined;
  }

  setIconAnchor(marker: IMarker, anchor: SnLonLatLike): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setIconAnchor(googleMarker, marker, anchor);
    }
  }

  getIconAnchor(marker: IMarker): SnLonLat | undefined {
    return this._styleService.getIconAnchor(marker);
  }

  setCustomData(marker: IMarker, data: unknown): void {
    const googleMarker = this._googleMarkers.get(marker.id);
    if (googleMarker) {
      this._styleService.setCustomData(googleMarker, marker, data);
    }
  }

  getCustomData(marker: IMarker): unknown {
    return this._styleService.getCustomData(marker);
  }

  destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();

    this.getAllMarkers().forEach((marker) => this.removeMarker(marker));

    this._googleMarkers.clear();
  }
}
